import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { RouteName } from '../router.js';

/**
 * Screens other than the Camp load as their own chunks, so the first paint
 * only pays for the shell and the Camp. The service worker precaches every
 * chunk, and `prefetchScreens` warms them right after start-up, so offline
 * play and navigation stay instant.
 */
const loaders = {
  summon: () => import('./Summon.js').then((m) => ({ default: m.Summon })),
  collection: () => import('./Collection.js').then((m) => ({ default: m.Collection })),
  team: () => import('./Team.js').then((m) => ({ default: m.Team })),
  adventures: () => import('./Adventures.js').then((m) => ({ default: m.Adventures })),
  missions: () => import('./Missions.js').then((m) => ({ default: m.Missions })),
  daily: () => import('./Daily.js').then((m) => ({ default: m.Daily })),
  quests: () => import('./Quests.js').then((m) => ({ default: m.Quests })),
  attributes: () => import('./Attributes.js').then((m) => ({ default: m.Attributes })),
  games: () => import('./Games.js').then((m) => ({ default: m.Games })),
  success: () => import('./Success.js').then((m) => ({ default: m.Success })),
  stats: () => import('./Stats.js').then((m) => ({ default: m.Stats })),
  settings: () => import('./Settings.js').then((m) => ({ default: m.Settings })),
} satisfies Partial<Record<RouteName, () => Promise<{ default: ComponentType }>>>;

export type LazyRoute = keyof typeof loaders;

export const lazyScreens = Object.fromEntries(Object.entries(loaders).map(([name, load]) => [name, lazy(load)])) as Record<LazyRoute, LazyExoticComponent<ComponentType>>;

/** Fetches every screen chunk in the background (idle time, once). */
let prefetched = false;
export function prefetchScreens(): void {
  if (prefetched) return;
  prefetched = true;
  for (const load of Object.values(loaders)) void load().catch(() => undefined);
}
